import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '../../components/CardShell';
import MonthYearPicker from '../../components/MonthYearPicker';
import { Cell, Choix } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { getLoans, createLoan, patchLoan, deleteLoan, setLoanPayment } from '../../api/financials_loans';
import { getDebts } from '../../api/financials_debts';
import './FinancingPlanPage.css';

/* LE MODE CALCULÉ D'EMPRUNTS ET APPORTS (2026-10-03, « bientôt disponible ») :
   les contrats, rejoués depuis leur début, et leur RAPPROCHEMENT avec les
   comptes du bilan à la clôture. Il reste un CONTRÔLE : il ne nourrit plus ni
   le bilan, ni le plan de liquidité, ni le compte de résultat, que le mode
   Manuel alimente (`DebtManualCard`). Chaque contrat dit le compte qu'il
   rembourse. */

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function FinancingPlanPage({ activeTenant, year, unit = 'kchf', addToast }) {
  const p = useP();
  const tid = activeTenant?.id;
  const k = unit === 'chf' ? 1 : 1000;
  const [view, setView] = useState(null);
  /* RAPPROCHEMENT (2026-09-26, par compte depuis le 2026-10-03) : l'encours
     rejoué de chaque contrat face au solde du compte qu'il rembourse. L'écart
     ne se corrige PAS, il s'affiche : un emprunt oublié, un taux ou une durée
     faux, des intérêts courus restés sur la dette. */
  const [debts, setDebts] = useState(null);

  const load = useCallback(() => {
    if (!tid) return;
    getLoans(tid, year).then((r) => setView(r.error ? null : r.data)).catch(() => setView(null));
    getDebts(tid, year).then((r) => setDebts(r && !r.error ? r.data : null)).catch(() => setDebts(null));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  const fmt = (v) => {
    const x = num(v) / k;
    if (Math.abs(x) < 0.005) return '—';
    return x.toLocaleString('de-CH', { maximumFractionDigits: unit === 'chf' ? 0 : 1 });
  };
  const err = () => addToast && addToast(p('Enregistrement impossible'), 'error');
  const save = (pr) => pr.then((r) => { setView(r.error ? view : r.data); load(); }).catch(err);

  const rows = view?.rows || [];
  /* LA DATE FAIT LA NOUVEAUTÉ (décision Olivier, 2026-09-26) : un emprunt en
     cours et un financement nouveau se saisissaient dans deux cartes, la
     seconde demandant de retaper un capital restant dû à chaque clôture. On
     pose désormais le CONTRAT, une seule fois ; le tirage n'entre en
     trésorerie que s'il tombe après l'arrêté, et l'encours se rejoue. Ne reste
     qu'une distinction de NATURE, le compte courant, qui n'est pas un contrat
     et se saisit mois par mois. */
  const loans = rows.filter((r) => (r.kind || 'new') !== 'current');

  const add = (kind) => save(createLoan(tid, year, {
    kind, label: '', principal: 0, rate_pct: null, start_year: year, start_month: 1,
    repay_mode: 'fixed', repay_freq: 'monthly',
    term_months: null, first_repay_year: null, first_repay_month: null,
  }));
  const patch = (id, payload) => save(patchLoan(tid, id, year, payload));

  /* ⚠️ UN MONTANT SE TAPE DANS L'UNITÉ DE L'ÉCRAN : en kCHF, « 450 » vaut
     450'000. `money` habille la cellule, `toBase` rend la valeur à la base —
     les deux vont ENSEMBLE, sans quoi un prêt de 450'000 s'enregistre à 450
     francs sans que rien ne le signale. */
  const money = (v) => ({
    value: v, type: 'number',
    format: (x) => (x == null || Number(x) === 0 ? '' : fmt(x)),
    toDraft: (x) => (x == null || Number(x) === 0 ? '' : String(num(x) / k)),
  });
  const toBase = (v) => (v == null ? null : v * k);


  const accounts = debts?.available || [];
  const accOf = (no) => accounts.find((x) => x.account === no) || null;
  const cc = num(debts?.closed_count);
  const at = cc >= 1
    ? `${String(new Date(year, cc, 0).getDate()).padStart(2, '0')}.${String(cc).padStart(2, '0')}.${year}`
    : `31.12.${year - 1}`;
  const recon = (list) => {
    const bal = (r) => (r.account_number && accOf(r.account_number) ? num(accOf(r.account_number).opening) : null);
    const seen = new Set();
    const totBal = list.reduce((s2, r) => {
      if (!r.account_number || seen.has(r.account_number) || bal(r) == null) return s2;
      seen.add(r.account_number);
      return s2 + bal(r);
    }, 0);
    const totCalc = list.reduce((s2, r) => s2 + num(r.at_close), 0);
    return (
      <CardShell className="report-card" title={`${p('Rapprochement au')} ${at}`}>
        <table className="lk-entry-table fpl-tbl">
          <thead>
            <tr>
              <th className="l">{p('Contrat')}</th><th className="l">{p('Compte')}</th>
              <th>{p('Encours calculé')}</th><th>{p('Solde au bilan')}</th><th>{p('Écart')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => {
              const b = bal(r);
              const gap = b == null ? null : Math.round((num(r.at_close) - b) * 100) / 100;
              const a = r.account_number ? accOf(r.account_number) : null;
              return (
                <tr key={r.id}>
                  <td className="l">{r.label || '—'}</td>
                  <td className="l">{a ? `${a.account} · ${a.name || a.line_label}` : <span className="fpl-ro">—</span>}</td>
                  <td>{fmt(r.at_close)}</td>
                  <td>{b == null ? <span className="fpl-ro">—</span> : fmt(b)}</td>
                  <td className={gap && Math.abs(gap) / k >= 0.005 ? 'fpl-gap' : ''}>{gap == null ? '—' : fmt(gap)}</td>
                </tr>
              );
            })}
            {list.length > 0 && (
              <tr className="lk-hi-strong">
                <td className="l">{p('Total')}</td><td />
                <td>{fmt(totCalc)}</td><td>{fmt(totBal)}</td>
                <td className={Math.abs(totCalc - totBal) / k >= 0.005 ? 'fpl-gap' : ''}>{fmt(totCalc - totBal)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </CardShell>
    );
  };

  /* UNE TABLE, DEUX CARTES : un emprunt repris et un financement nouveau se
     saisissent pareil, seul le cash diffère, et il est porté par la nature. */
  const loanTable = (list) => (
    <>
          <div className="lk-hscroll">
            <table className="lk-entry-table fpl-tbl">
              <colgroup>
                <col style={{ width: '160px' }} /><col style={{ width: '150px' }} /><col style={{ width: '92px' }} />
                <col style={{ width: '62px' }} /><col style={{ width: '92px' }} />
                <col style={{ width: '70px' }} /><col style={{ width: '92px' }} />
                <col style={{ width: '92px' }} /><col style={{ width: '100px' }} />
                <col style={{ width: '30px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="l">{p('Contrat')}</th>
                  <th className="l">{p('Compte')}</th>
                  <th>{p('Montant')}</th>
                  <th>{p('Taux')}</th>
                  <th className="l">{p('Début')}</th>
                  <th>{p('Durée')}<i className="fpl-u">({p('mois')})</i></th>
                  <th className="l">{p('Fin')}</th>
                  <th className="l">{p('Amort.')}</th>
                  <th className="l">{p('1er paiement')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td className="l">
                        <Cell value={r.label} canWrite placeholder={p('Prêt bancaire')}
                              onSave={(v) => patch(r.id, { label: v || '' })} />
                      </td>
                      <td className="l">
                        <Choix value={r.account_number || ''} canWrite
                               options={[{ value: '', label: '—' },
                                 ...accounts.map((a) => ({ value: a.account, label: `${a.account} · ${a.name || a.line_label}` }))]}
                               onSave={(v) => patch(r.id, { account_number: v || null })} />
                      </td>
                      <td><Cell {...money(r.principal)} canWrite onSave={(v) => patch(r.id, { principal: toBase(v) || 0 })} /></td>
                      <td>
                        <Cell value={r.rate_pct == null ? null : Math.round(r.rate_pct * 10000) / 100}
                              type="number" format={(x) => (x == null ? '' : `${x} %`)}
                              toDraft={(x) => (x == null ? '' : String(x))} canWrite
                              onSave={(v) => patch(r.id, v == null ? { clear_rate: true } : { rate_pct: v / 100 })} />
                      </td>
                      <td className="l">
                        <MonthYearPicker month={r.start_month || 1} year={r.start_year} canWrite
                                         ariaLabel={p('Début')}
                                         onChange={(m, y) => patch(r.id, { start_month: m, start_year: y })} />
                      </td>
                      <td>
                        {/* LA DURÉE, PAS L'ÉCHÉANCE (2026-09-26) : on pose le
                            terme du prêt, le capital par échéance s'en déduit. */}
                        <Cell value={r.term_months} type="number" canWrite placeholder="—"
                              format={(x) => (x == null ? '' : `${x} ${p('mois')}`)}
                              toDraft={(x) => (x == null ? '' : String(x))}
                              onSave={(v) => patch(r.id, v == null ? { clear_repay: true } : { term_months: Number(v) || null })} />
                      </td>
                      <td className="l fpl-ro">
                        {r.end_year ? `${MONTHS[(r.end_month || 1) - 1]} ${r.end_year}` : '—'}
                      </td>
                      <td className="l">
                        {/* « Manuel » n'est plus proposé ; une ligne qui le porte
                            déjà garde son libellé, sans quoi la cellule mentirait. */}
                        <Choix value={r.repay_mode === 'manual' ? 'manual' : (r.repay_freq || 'monthly')}
                               canWrite
                               options={[{ value: 'monthly', label: p('Mensuel') },
                                 { value: 'quarterly', label: p('Trimestriel') },
                                 { value: 'yearly', label: p('Annuel') },
                                 { value: 'bullet', label: p('À l\'échéance') },
                                 ...(r.repay_mode === 'manual' ? [{ value: 'manual', label: p('Manuel') }] : [])]}
                               onSave={(v) => patch(r.id, v === 'manual'
                                 ? { repay_mode: 'manual' }
                                 : { repay_mode: 'fixed', repay_freq: v })} />
                      </td>
                      <td className="l">
                        {(r.repay_mode === 'manual' || r.repay_freq === 'bullet') ? <span className="fpl-ro">—</span> : (
                          <MonthYearPicker month={r.first_repay_month || r.start_month || 1}
                                           year={r.first_repay_year || r.start_year} canWrite
                                           ariaLabel={p('1er paiement')}
                                           onChange={(m, y) => patch(r.id, { first_repay_month: m, first_repay_year: y })} />
                        )}
                      </td>
                      <td>
                        <button type="button" className="lk-x lk-del" aria-label={p('Supprimer')}
                                onClick={() => save(deleteLoan(tid, r.id, year))}>×</button>
                      </td>
                    </tr>
                    {r.repay_mode === 'manual' && (
                      <tr className="fpl-pay">
                        <td className="l">{p('remboursements')} {year}</td>
                        <td colSpan={9}>
                          <span className="fpl-months">
                            {MONTHS.map((mo, i) => (
                              <span key={mo} className="fpl-mo">
                                <i>{mo}</i>
                                <Cell {...money(r.months?.[i + 1]?.repay)} canWrite
                                      onSave={(v) => save(setLoanPayment(tid, r.id, year, i + 1, toBase(v)))} />
                              </span>
                            ))}
                          </span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {!list.length && (
                  <tr><td className="l" colSpan={10}>{p('Aucun contrat posé.')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
    </>
  );
  return (
    <div className="fpl">
      <CardShell className="report-card"
                 title={<>{p('Contrats d\'emprunt')}<span className="lk-soon">{p('bientôt disponible')}</span></>}
                 primaryAction={(
                   <button type="button" className="fpl-new" onClick={() => add('loan')}>
                     ＋ {p('Emprunt')}
                   </button>
                 )}>
        {loanTable(loans)}
      </CardShell>
      {loans.length > 0 && recon(loans)}
    </div>
  );
}
